import { ReactNode } from 'react';

interface PillButtonProps {
  children: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  href?: string;
  selected?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  className?: string;
}

export default function PillButton({
  children,
  icon,
  onClick,
  href,
  selected = false,
  disabled = false,
  type = 'button',
  ariaLabel,
  className = '',
}: PillButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center gap-2 px-4 py-2 md:py-2.5 rounded-full border transition-all duration-200 text-[14px] font-medium min-h-[44px]';

  const getStateClasses = () => {
    if (disabled) {
      return 'cursor-not-allowed';
    }
    return 'active:scale-95';
  };

  const getStateStyles = () => {
    if (disabled) {
      return {
        borderColor: 'rgba(255, 255, 255, 0.10)',
        color: 'rgba(255, 255, 255, 0.35)',
        backgroundColor: 'transparent',
      };
    }

    if (selected) {
      return {
        borderColor: '#40E0D0',
        color: '#40E0D0',
        backgroundColor: 'rgba(64, 224, 208, 0.10)',
      };
    }

    return {
      borderColor: 'rgba(255, 255, 255, 0.20)',
      color: 'rgba(255, 255, 255, 0.85)',
      backgroundColor: 'transparent',
    };
  };

  const updateIconColor = (element: HTMLElement, color: string) => {
    const iconWrapper = element.querySelector('.icon-wrapper') as HTMLElement;
    if (iconWrapper) {
      iconWrapper.style.color = color;

      const appleMusicBg = iconWrapper.querySelector('.apple-music-bg') as HTMLElement;
      if (appleMusicBg) {
        const appleMusicSvg = appleMusicBg.querySelector('svg') as SVGElement;
        if (color === '#40E0D0') {
          appleMusicBg.style.backgroundColor = '#40E0D0';
          if (appleMusicSvg) appleMusicSvg.style.color = 'rgba(0, 0, 0, 0.85)';
        } else {
          appleMusicBg.style.backgroundColor = 'rgba(255, 255, 255, 0.85)';
          if (appleMusicSvg) appleMusicSvg.style.color = '#000';
        }
      }
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || selected) return;
    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.70)';
    e.currentTarget.style.color = '#40E0D0';
    e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
    updateIconColor(e.currentTarget, '#40E0D0');
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || selected) return;
    const defaultStyles = getStateStyles();
    e.currentTarget.style.borderColor = defaultStyles.borderColor;
    e.currentTarget.style.color = defaultStyles.color;
    e.currentTarget.style.backgroundColor = defaultStyles.backgroundColor;
    updateIconColor(e.currentTarget, defaultStyles.color);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || selected) return;
    e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.10)';
    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.85)';
    e.currentTarget.style.color = '#40E0D0';
    updateIconColor(e.currentTarget, '#40E0D0');
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || selected) return;
    e.currentTarget.style.backgroundColor = 'rgba(64, 224, 208, 0.06)';
    e.currentTarget.style.borderColor = 'rgba(64, 224, 208, 0.70)';
    e.currentTarget.style.color = '#40E0D0';
    updateIconColor(e.currentTarget, '#40E0D0');
  };

  const focusClasses = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111111]';
  const focusRingStyle = disabled ? {} : { '--tw-ring-color': 'rgba(64, 224, 208, 0.45)' } as React.CSSProperties;

  const combinedClasses = `${baseClasses} ${getStateClasses()} ${focusClasses} ${className}`;

  const getIconColor = () => {
    if (disabled) return 'rgba(255, 255, 255, 0.35)';
    if (selected) return '#40E0D0';
    return 'rgba(255, 255, 255, 0.85)';
  };

  const content = (
    <>
      {icon && (
        <span
          className="flex-shrink-0 transition-colors duration-200 icon-wrapper"
          style={{ color: getIconColor() }}
          data-default-icon-color="rgba(255, 255, 255, 0.85)"
        >
          {icon}
        </span>
      )}
      <span className="transition-colors duration-200">{children}</span>
    </>
  );

  if (href && !disabled) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        className={combinedClasses}
        style={{ ...getStateStyles(), ...focusRingStyle }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onClick={(e) => {
          if (onClick) {
            onClick();
          }
        }}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={combinedClasses}
      style={{ ...getStateStyles(), ...focusRingStyle }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      {content}
    </button>
  );
}
